import { useEffect, useRef, useState } from "react";
import { IScenarioFormField, IScenarioNode } from "../../models/ILiveChatScenario";
import { SAMPLE_TOKEN_VALUES, TYPE_META, findNode, parseConfig, resolvePreview, sortNodes } from "../../utils/scenarioUtils";

interface Props {
  tree: IScenarioNode[];
  /** Verilirse önizleme bu adım seçilmiş gibi başlar. */
  startNodeId: string | null;
  onEditNode: (node: IScenarioNode) => void;
}

type Bubble = { id: number; from: "bot" | "user" | "system"; text: string; nodeId?: string };
type Widget =
  | { kind: "chips"; options: IScenarioNode[] }
  | { kind: "otp"; node: IScenarioNode }
  | { kind: "form"; node: IScenarioNode; fields: IScenarioFormField[]; submitLabel: string }
  | { kind: "end"; reason: "branch" | "handoff" | "closed" | "empty" };

// Backend'deki sabitlerle aynı
const MAX_AUTO_ADVANCE = 4;
const DEFAULT_PROMPT = "Aşağıdaki seçeneklerden birini seçebilirsiniz.";
const MASKED_PHONE = "+**********33";

let bubbleSeq = 0;
const bubble = (from: Bubble["from"], text: string, nodeId?: string): Bubble => ({ id: ++bubbleSeq, from, text, nodeId });

export default function ScenarioPreview({ tree, startNodeId, onEditNode }: Props) {
  const [orderLinked, setOrderLinked] = useState(true);
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [widget, setWidget] = useState<Widget>({ kind: "end", reason: "empty" });
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [ticketSeq, setTicketSeq] = useState(1);
  const bodyRef = useRef<HTMLDivElement>(null);

  const activeChildren = (nodes: IScenarioNode[]) => sortNodes(nodes).filter((n) => n.isActive);
  const roots = activeChildren(tree);

  /* ---------------------- Backend akış kurallarının kopyası ---------------------- */

  const nextTicketNo = () => {
    const no = `TCK-${String(1000 + ticketSeq).padStart(6, "0")}`;
    setTicketSeq((s) => s + 1);
    return no;
  };

  const reveal = (children: IScenarioNode[], depth: number, prompt: string | null, out: Bubble[], parentId?: string): Widget => {
    const kids = activeChildren(children);
    const auto = kids.length === 1 && kids[0].nodeType !== "Button" && depth < MAX_AUTO_ADVANCE;
    if (kids.length === 0 || auto) {
      if (prompt) out.push(bubble("bot", prompt, parentId));
      return auto ? run(kids[0], depth + 1, out) : { kind: "end", reason: "branch" };
    }
    out.push(bubble("bot", prompt || DEFAULT_PROMPT, parentId));
    return { kind: "chips", options: kids };
  };

  const run = (node: IScenarioNode, depth: number, out: Bubble[]): Widget => {
    const config = parseConfig(node.configJson);
    let content = resolvePreview(node.content, config, orderLinked);

    if (node.nodeType === "Otp") {
      out.push(bubble("bot", content || `Telefonunuza (${MASKED_PHONE}) bir doğrulama kodu gönderdik.`, node.id));
      return { kind: "otp", node };
    }
    if (node.nodeType === "Form") {
      out.push(bubble("bot", content || node.text, node.id));
      return { kind: "form", node, fields: config.form?.fields ?? [], submitLabel: config.form?.submitLabel || "Gönder" };
    }
    if (node.nodeType === "Ticket" || node.action === "CreateTicket") {
      const confirmation = `Talebiniz ${nextTicketNo()} numarasıyla oluşturuldu. Ekibimiz en kısa sürede size dönüş yapacak.`;
      content = content ? `${content} ${confirmation}` : confirmation;
    }
    if (node.action === "HandoffToAgent") {
      if (content) out.push(bubble("bot", content, node.id));
      out.push(bubble("system", "Sohbet temsilci kuyruğuna alındı — bir temsilci birazdan bağlanacak."));
      return { kind: "end", reason: "handoff" };
    }
    if (node.action === "CloseConversation") {
      if (content) out.push(bubble("bot", content, node.id));
      out.push(bubble("system", "Sohbet sonlandırıldı."));
      return { kind: "end", reason: "closed" };
    }
    return reveal(node.children ?? [], depth, content, out, node.id);
  };

  const push = (out: Bubble[], next: Widget) => {
    setBubbles((b) => [...b, ...out]);
    setWidget(next);
    setFormValues({});
    setFormError("");
    setOtpCode("");
  };

  const select = (node: IScenarioNode) => {
    const out: Bubble[] = [bubble("user", node.text)];
    push(out, run(node, 0, out));
  };

  const restart = (startId: string | null = null) => {
    const out: Bubble[] = [bubble("bot", `Merhaba ${SAMPLE_TOKEN_VALUES["customer.name"]}! Mutfacik canlı desteğe hoş geldiniz. Size nasıl yardımcı olabilirim?`)];
    const startNode = startId ? findNode(tree, startId) : null;
    if (startNode) {
      out.push(bubble("system", `"${startNode.text}" adımından başlatıldı`));
      out.push(bubble("user", startNode.text));
      const next = run(startNode, 0, out);
      setBubbles(out);
      setWidget(next);
    } else {
      setBubbles(out);
      setWidget(roots.length ? { kind: "chips", options: roots } : { kind: "end", reason: "empty" });
    }
    setFormValues({});
    setFormError("");
    setOtpCode("");
  };

  // Ağaç ya da başlangıç adımı değişince baştan başla
  useEffect(() => {
    restart(startNodeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, startNodeId, orderLinked]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
  }, [bubbles, widget]);

  const verifyOtp = (node: IScenarioNode) => {
    if (!/^\d{4,8}$/.test(otpCode.trim())) return;
    const out: Bubble[] = [bubble("user", "••••••"), bubble("bot", "Telefon numaranız doğrulandı.", node.id)];
    push(out, reveal(node.children ?? [], MAX_AUTO_ADVANCE - 1, null, out, node.id));
  };

  const submitForm = (node: IScenarioNode, fields: IScenarioFormField[]) => {
    const missing = fields.find((f) => f.required && !(formValues[f.name] ?? "").trim());
    if (missing) { setFormError(`"${missing.label}" alanı zorunludur.`); return; }
    const summary = fields.map((f) => `${f.label}: ${formValues[f.name] || "-"}`).join("\n");
    const confirmation = `Bilgileriniz alındı. Talebiniz ${nextTicketNo()} numarasıyla oluşturuldu; ekibimiz en kısa sürede size dönüş yapacak.`;
    const out: Bubble[] = [bubble("user", summary)];
    push(out, reveal(node.children ?? [], MAX_AUTO_ADVANCE - 1, confirmation, out, node.id));
  };

  const editFromBubble = (nodeId?: string) => {
    const n = nodeId ? findNode(tree, nodeId) : null;
    if (n) onEditNode(n);
  };

  return (
    <div className="lcs-preview-layout">
      <div className="lcs-phone">
        <div className="lcs-phone-head">
          <span style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="bi bi-robot"></i>
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: ".9rem" }}>Mutfacik Asistan</div>
            <div style={{ fontSize: ".7rem", opacity: 0.85 }}>Önizleme — gerçek SMS / ticket gönderilmez</div>
          </div>
          <button type="button" onClick={() => restart(null)} title="Baştan başlat"
            style={{ background: "rgba(255,255,255,.18)", border: "none", color: "#fff", borderRadius: 10, width: 34, height: 34, cursor: "pointer" }}>
            <i className="bi bi-arrow-counterclockwise"></i>
          </button>
        </div>

        <div className="lcs-phone-body" ref={bodyRef}>
          {bubbles.map((b) => (
            <div key={b.id} className={`lcs-bubble lcs-bubble--${b.from}`}
              onDoubleClick={() => editFromBubble(b.nodeId)}
              title={b.nodeId ? "Düzenlemek için çift tıklayın" : undefined}
              style={b.nodeId ? { cursor: "pointer" } : undefined}>
              {b.text}
            </div>
          ))}

          {widget.kind === "chips" && widget.options.map((o) => (
            <button key={o.id} type="button" className="lcs-option" onClick={() => select(o)}>
              {o.text}
              {(o.children ?? []).some((c) => c.isActive) && <i className="bi bi-chevron-right" style={{ fontSize: ".7rem" }}></i>}
            </button>
          ))}

          {widget.kind === "otp" && (
            <div className="lcs-widget">
              <div style={{ fontSize: ".78rem", fontWeight: 700, color: "#374151", marginBottom: 6 }}>
                <i className="bi bi-shield-lock-fill me-1" style={{ color: "#d97706" }}></i>Doğrulama kodu
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <input className="lcs-input" inputMode="numeric" maxLength={8} value={otpCode} placeholder="Herhangi bir 6 haneli kod"
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => { if (e.key === "Enter") verifyOtp(widget.node); }} />
                <button type="button" className="lcs-btn lcs-btn--primary" disabled={!/^\d{4,8}$/.test(otpCode)} onClick={() => verifyOtp(widget.node)}>Doğrula</button>
              </div>
              <div className="lcs-hint">Önizlemede her kod kabul edilir.</div>
            </div>
          )}

          {widget.kind === "form" && (
            <div className="lcs-widget">
              <div style={{ fontSize: ".82rem", fontWeight: 800, color: "#111827", marginBottom: 8 }}>{widget.node.text}</div>
              {widget.fields.length === 0 && <div className="lcs-hint" style={{ marginTop: 0 }}>Bu formda alan yok.</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {widget.fields.map((f) => (
                  <div key={f.name}>
                    <div className="lcs-label" style={{ marginBottom: 3 }}>
                      <span>{f.label}{f.required && <span style={{ color: "#dc2626" }}> *</span>}</span>
                    </div>
                    {f.type === "textarea" ? (
                      <textarea className="lcs-input" rows={2} maxLength={f.maxLength || 500} value={formValues[f.name] ?? ""}
                        onChange={(e) => setFormValues((v) => ({ ...v, [f.name]: e.target.value }))} />
                    ) : f.type === "select" ? (
                      <select className="lcs-input" value={formValues[f.name] ?? ""} onChange={(e) => setFormValues((v) => ({ ...v, [f.name]: e.target.value }))}>
                        <option value="">Seçiniz</option>
                        {(f.options ?? []).filter((o) => o.trim()).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input className="lcs-input" maxLength={f.maxLength || 500}
                        type={f.type === "email" ? "email" : f.type === "number" ? "number" : f.type === "phone" ? "tel" : "text"}
                        value={formValues[f.name] ?? ""} onChange={(e) => setFormValues((v) => ({ ...v, [f.name]: e.target.value }))} />
                    )}
                  </div>
                ))}
              </div>
              {formError && <div className="lcs-hint" style={{ color: "#dc2626" }}>{formError}</div>}
              <button type="button" className="lcs-btn lcs-btn--primary" style={{ width: "100%", marginTop: 10 }} onClick={() => submitForm(widget.node, widget.fields)}>
                {widget.submitLabel}
              </button>
            </div>
          )}

          {widget.kind === "end" && (
            <div style={{ alignSelf: "center", textAlign: "center", marginTop: 6 }}>
              <div style={{ fontSize: ".74rem", color: "#9ca3af", marginBottom: 6 }}>
                {widget.reason === "empty" && "Aktif açılış butonu yok — müşteriye eski hazır yanıtlar gösterilir."}
                {widget.reason === "branch" && "Bu dal burada bitiyor."}
                {widget.reason === "handoff" && "Akış temsilciye devredildi."}
                {widget.reason === "closed" && "Sohbet kapandı."}
              </div>
              <button type="button" className="lcs-btn" onClick={() => restart(null)}><i className="bi bi-arrow-counterclockwise"></i>Baştan başla</button>
            </div>
          )}
        </div>
      </div>

      {/* Yan panel */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="lcs-card" style={{ padding: 16 }}>
          <div className="lcs-section-title"><i className="bi bi-sliders"></i>Önizleme ayarları</div>
          <div className="lcs-label">Sohbete bağlı sipariş</div>
          <div className="lcs-chips">
            <button type="button" className={`lcs-chip${orderLinked ? " lcs-chip--active" : ""}`} onClick={() => setOrderLinked(true)}>
              <i className="bi bi-bag-check"></i>Sipariş var
            </button>
            <button type="button" className={`lcs-chip${!orderLinked ? " lcs-chip--active" : ""}`} onClick={() => setOrderLinked(false)}>
              <i className="bi bi-bag-x"></i>Sipariş yok
            </button>
          </div>
          <div className="lcs-hint">Sipariş yokken sipariş değişkenleri dolmaz; adımın alternatif metni gösterilir.</div>
        </div>

        <div className="lcs-card" style={{ padding: 16 }}>
          <div className="lcs-section-title"><i className="bi bi-info-circle"></i>Akış kuralları</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: ".8rem", color: "#4b5563", display: "flex", flexDirection: "column", gap: 6 }}>
            <li>Yalnızca <b>aktif</b> adımlar gösterilir; pasif bir adımın altındaki her şey de gizlenir.</li>
            <li>Bir adımın <b>tek alt adımı</b> varsa ve buton değilse, müşteri tıklamadan otomatik çalışır (bir tıkta en fazla {MAX_AUTO_ADVANCE} adım).</li>
            <li><b>Temsilciye bağla</b> ve <b>Sohbeti kapat</b> adımlarından sonra alt adımlar sunulmaz.</li>
            <li><b>Doğrulama</b> adımının altındaki dal SMS kodu girilmeden açılmaz.</li>
            <li>Asistan balonuna çift tıklayarak o adımı düzenleyebilirsiniz.</li>
          </ul>
        </div>

        <div className="lcs-card" style={{ padding: 16 }}>
          <div className="lcs-section-title"><i className="bi bi-palette"></i>Adım türleri</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {Object.entries(TYPE_META).map(([k, m]) => (
              <span key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: ".8rem", color: "#374151", fontWeight: 600 }}>
                <span className="lcs-type-icon" style={{ width: 24, height: 24, borderRadius: 7, fontSize: ".72rem", background: m.bg, color: m.color }}>
                  <i className={`bi ${m.icon}`}></i>
                </span>
                {m.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
